/**
 * ActionsPanel — composite that brings together tags, qualifiers, and the
 * grid of PlayerCards (each with its action buttons).
 *
 * The actual click handlers are delegated to the screen layer:
 *   - onAction(player, actionCode, points)
 *   - onTagClick(tag) — called by useTags.handleTagClick discriminator
 *   - onToggleQualifier(qualifier)
 *   - onMore(player) — opens the MoreActionsSheet
 *   - onSubstitute(player) — bench him
 *
 * Layout adapts: 1/2/3/4 columns of PlayerCards depending on width.
 */

import React from 'react';
import { Box, Typography } from '@mui/material';
import { useStatsTheme } from '../theme/statsTheme';
import { ACTION_CODES } from '../../utils/actionUtils';
import ActionButton from './ActionButton';
import PlayerCard from './PlayerCard';
import TagChip from './TagChip';
import QualifierToggle from './QualifierToggle';

function PlayerActionGrid({ player, onAction, t, compact = false }) {
  const theme = useStatsTheme();

  const tt = (key, fallback) => (typeof t === 'function' ? t(`estadisticasNew.${key}`, { defaultValue: fallback }) : fallback);

  // Row 1: shooting (split 2pts / 3pts / FT, made/missed pairs)
  const shotRows = [
    { code2: ACTION_CODES.TWO_PTS_MADE, code3: ACTION_CODES.TWO_PTS_MISSED, label: '2PT' },
    { code2: ACTION_CODES.THREE_PTS_MADE, code3: ACTION_CODES.THREE_PTS_MISSED, label: '3PT' },
    { code2: ACTION_CODES.FREE_THROW_MADE, code3: ACTION_CODES.FREE_THROW_MISSED, label: 'FT' },
  ];

  const btnSx = compact ? { height: 26, minWidth: 26, fontSize: 10, px: 0.6, boxShadow: 'none' } : undefined;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: compact ? 0.35 : 0.75 }}>
      {shotRows.map((row) => (
        <Box key={row.label} sx={{ display: 'flex', gap: compact ? 0.35 : 0.6, alignItems: 'center' }}>
          <Typography
            sx={{
              width: 28,
              fontSize: 10,
              fontWeight: 800,
              color: theme.text.secondary,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
            }}
          >
            {row.label}
          </Typography>
          <ActionButton
            variant="made"
            label={tt('made', '✓')}
            size="sm"
            fullWidth
            sx={btnSx}
            onClick={() => onAction(player, row.code2, row.label === '2PT' ? 2 : row.label === '3PT' ? 3 : 1)}
            ariaLabel={`${row.label} made for ${player.nom}`}
          />
          <ActionButton
            variant="missed"
            label={tt('missed', '✗')}
            size="sm"
            fullWidth
            sx={btnSx}
            onClick={() => onAction(player, row.code3, 0)}
            ariaLabel={`${row.label} missed for ${player.nom}`}
          />
        </Box>
      ))}

      {/* Row: rebounds + assist + foul */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: compact ? 0.35 : 0.6,
          mt: compact ? 0.15 : 0.4,
        }}
      >
        <ActionButton
          variant="info"
          label={tt('rebOff', 'REB+')}
          size="sm"
          fullWidth
          sx={btnSx}
          onClick={() => onAction(player, ACTION_CODES.OFFENSIVE_REBOUND, 0)}
        />
        <ActionButton
          variant="info"
          label={tt('rebDef', 'REB-')}
          size="sm"
          fullWidth
          sx={btnSx}
          onClick={() => onAction(player, ACTION_CODES.DEFENSIVE_REBOUND, 0)}
        />
        <ActionButton
          variant="neutral"
          label={tt('assist', 'AST')}
          size="sm"
          fullWidth
          sx={btnSx}
          onClick={() => onAction(player, ACTION_CODES.ASSIST, 0)}
        />
        <ActionButton
          variant="warning"
          label={tt('foul', 'FOUL')}
          size="sm"
          fullWidth
          sx={btnSx}
          onClick={() => onAction(player, ACTION_CODES.FOUL, 0)}
        />
      </Box>
    </Box>
  );
}

export default function ActionsPanel({
  activePlayers = [],
  tags = [],
  activeQualifiers = [],
  onAction,
  onTagClick,
  onToggleQualifier,
  onMore,
  onSubstitute,
  playerTimeTracking = {},
  // Optional pre-computed map { [playerId]: seconds }. When provided, takes
  // precedence over the local computation from playerTimeTracking. The
  // screen layer (StatsRouter) computes this map with the open segment
  // included so live values tick.
  timeOnFloorByPlayerId,
  fouls = {},
  renderPlayerCard,
  compact = false,
  showTags = true,
  showQualifiers = true,
  showPlayers = true,
  playersLayout = 'grid', // 'grid' | 'row'
  t,
}) {
  const theme = useStatsTheme();
  const tt = (key, fallback) => (typeof t === 'function' ? t(`estadisticasNew.${key}`, { defaultValue: fallback }) : fallback);

  const computeTimeOnFloor = (playerId) => {
    if (timeOnFloorByPlayerId && typeof timeOnFloorByPlayerId[playerId] === 'number') {
      return timeOnFloorByPlayerId[playerId];
    }
    const tracking = playerTimeTracking[playerId];
    if (!tracking) return 0;
    const closed = (tracking.realTimeSegments || []).reduce((acc, seg) => acc + Math.max(0, seg.out - seg.in), 0);
    return closed;
  };

  return (
    <Box
      sx={{
        backgroundColor: theme.surfaces.base,
        display: 'flex',
        flexDirection: 'column',
        gap: compact ? 0.6 : 1.2,
        p: compact ? 0.75 : 1.5,
        height: '100%',
        overflow: 'auto',
      }}
    >
      {/* Tags row */}
      {showTags && (
      <Box>
        {!compact && <Typography
          sx={{
            fontSize: 10,
            fontWeight: 700,
            color: theme.text.secondary,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            mb: 0.6,
          }}
        >
          {tt('quickTags', 'Quick tags')}
        </Typography>}
        <Box sx={{ display: 'flex', gap: 0.6, flexWrap: 'wrap', overflowX: 'auto' }}>
          {tags.length === 0 && (
            <Typography sx={{ fontSize: 11, color: theme.text.muted, fontStyle: 'italic' }}>
              {tt('noTags', 'No tags configured for this group')}
            </Typography>
          )}
          {tags.map((tag) => (
            <TagChip
              key={tag.id}
              tag={tag}
              size="sm"
              onClick={() => onTagClick && onTagClick(tag)}
              onDoubleClick={() => onTagClick && onTagClick(tag)}
            />
          ))}
        </Box>
      </Box>
      )}

      {/* Qualifiers */}
      {showQualifiers && (
      <Box>
        {!compact && <Typography
          sx={{
            fontSize: 10,
            fontWeight: 700,
            color: theme.text.secondary,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            mb: 0.6,
          }}
        >
          {tt('shotQualifiers', 'Shot qualifiers')}
        </Typography>}
        <QualifierToggle active={activeQualifiers} onToggle={onToggleQualifier} t={t} />
      </Box>
      )}

      {/* Player cards grid */}
      {showPlayers && (
      <Box
        sx={{
          display: 'grid',
          gap: compact ? 0.5 : 1.2,
          gridTemplateColumns: playersLayout === 'row'
            ? `repeat(${Math.max(activePlayers.length || 1, 1)}, minmax(0, 1fr))`
            : (compact ? '1fr 1fr' : {
              xs: '1fr',
              sm: '1fr 1fr',
              lg: '1fr 1fr',
              xl: '1fr 1fr 1fr',
            }),
          flex: 1,
          minHeight: 0,
        }}
      >
        {activePlayers.length === 0 && (
          <Box
            sx={{
              gridColumn: '1 / -1',
              p: 4,
              textAlign: 'center',
              border: `1px dashed ${theme.surfaces.border}`,
              borderRadius: theme.radius.md,
              color: theme.text.muted,
              fontSize: 13,
            }}
          >
            {tt('noActivePlayers', 'No players on the floor — drag players from the bench')}
          </Box>
        )}
        {activePlayers.map((player) => {
          const cardProps = {
            player,
            timeOnFloorSeconds: computeTimeOnFloor(player.id),
            fouls: fouls[player.id] || 0,
            onSubstitute,
            onMore,
            highlight: true,
            compact,
          };
          const inner = <PlayerActionGrid player={player} onAction={onAction} t={t} compact={compact} />;
          if (typeof renderPlayerCard === 'function') {
            return (
              <React.Fragment key={player.id}>
                {renderPlayerCard(player, cardProps, inner)}
              </React.Fragment>
            );
          }
          return (
            <PlayerCard key={player.id} {...cardProps}>
              {inner}
            </PlayerCard>
          );
        })}
      </Box>
      )}
    </Box>
  );
}
